/*
 * The command/event vocabulary of the mt-vox core (mt-vox docs/api.md), in
 * TypeScript. The web client speaks the same API as desktop and mobile so
 * UI behaviour and tests line up across platforms.
 */

export type Json = Record<string, unknown>;

export interface Command extends Json {
  id?: number;
  cmd: string;
}

export interface CoreEvent extends Json {
  ev: string;
}

export interface ResultEvent extends CoreEvent {
  ev: "result";
  id: number;
  ok: boolean;
  error?: string;
  message?: string;
  data?: Json;
}

export type CallState =
  | "incoming"
  | "outgoing"
  | "ringing"
  | "early"
  | "established"
  | "ended";

export interface CallInfo extends Json {
  call: string;
  account: string;
  state: CallState;
  dir: "in" | "out";
  peer: string;
  peer_name?: string;
  /** call.dial's ref (outgoing) */
  ref?: string;
  /** incoming: the INVITE's X- headers, names in lower case */
  headers?: Record<string, string>;
  on_hold: boolean;
  silenced: boolean;
  muted: boolean;
  conference: boolean;
  duration: number;
}

export interface AccountConfig {
  id: string;
  /** sip:user@domain */
  uri: string;
  auth_user?: string;
  password?: string;
  ha1?: string;
  display_name?: string;
  /** its position in the provisioning (1 = line 1) */
  line?: number;
  /** the outside number its calls show */
  caller_id?: string;
  register?: boolean;
  expires?: number;
}

export interface CoreSettings {
  user_agent?: string;
  /** WebSocket server of the SIP edge, e.g. wss://edge.example:8443 */
  wss_url: string;
  /** RTCPeerConnection ICE servers (TURN credentials included) */
  ice_servers?: RTCIceServer[];
  stats_interval?: number;
  log_level?: "debug" | "info" | "warn" | "error";
}

export type EventHandler = (ev: CoreEvent) => void;

export class CommandError extends Error {
  constructor(public code: string, message?: string) {
    super(message ?? code);
  }
}
